/**
 * Devoluciones — lista con timeline + acciones por estado.
 *
 * Una devolución puede ser:
 *   type = 'purchase'  → contra una OC del proveedor (entrada defectuosa).
 *                         Resolución típica: deduct (sale del inventario).
 *   type = 'sale'      → contra una venta al cliente (rechazo).
 *                         Resolución típica: restock_cold (vuelve a cuarto frío).
 *
 * Ciclo de vida (máquina de estados en el backend):
 *   requested → in_review → confirmed → resolved
 *                                    └→ composted   (después de 5 días sin recoger)
 *                       └→ cancelled
 *
 * Cada transición se acumula en `statusHistory[]` que renderizamos como
 * timeline vertical dentro del card expandido. `daysOpen` y `canCompost`
 * vienen calculados por el backend.
 */
import { useMemo, useState } from 'react'
import {
  Archive,
  ArchiveRestore,
  CheckCircle2,
  ChevronsRight,
  FileText,
  Leaf,
  Plus,
  Printer,
  RotateCcw,
  X,
} from 'lucide-react'
import { toast } from 'sonner'
import {
  RETURN_STATUSES,
  RETURN_STATUS_LABELS,
  RETURN_TYPES,
  RETURN_TYPE_LABELS,
  RETURN_RESOLUTIONS,
  RETURN_REASONS,
  RETURN_REASON_LABELS,
  INVENTORY_STAGES,
  INVENTORY_STAGE_LABELS,
} from '@expogreens/shared'
import { PageHeader } from '@/components/ui/PageHeader'
import { Button } from '@/components/ui/Button'
import { Modal, Field, Input, Select } from '@/components/ui/Modal'
import { ExpandableCards, CardField, CardGrid } from '@/components/ui/ExpandableCards'
import { useApi } from '@/lib/useApi'
import { useMutation } from '@/lib/useMutation'
import { apiCall } from '@/lib/api'
import { fmtDate, cn } from '@/lib/utils'

const STATUS_PILL = {
  requested: 'bg-gray-100 text-gray-700',
  in_review: 'bg-expo-yellow/30 text-expo-dark',
  confirmed: 'bg-blue-100 text-blue-700',
  resolved: 'bg-green-100 text-green-700',
  composted: 'bg-expo-olive/30 text-expo-dark',
  cancelled: 'bg-red-100 text-red-700',
}

const TIMELINE_DOT = {
  requested: 'bg-gray-400',
  in_review: 'bg-expo-yellow',
  confirmed: 'bg-blue-500',
  resolved: 'bg-green-500',
  composted: 'bg-expo-olive',
  cancelled: 'bg-red-500',
}

const TYPE_PILL = {
  purchase: 'bg-expo-primary/20 text-expo-primary',
  sale: 'bg-indigo-100 text-indigo-700',
}

const EMPTY_RETURN_ITEM = {
  variety: '',
  stemLength: 60,
  quantity: 0,
  productCondition: 'acceptable',
  // Doc: cada item debe tener su propio motivo de devolución porque en
  // un mismo pedido puede haber distintas razones por producto.
  reason: '',
}

export default function ReturnsPage() {
  const [filterType, setFilterType] = useState('all') // 'all' | 'purchase' | 'sale'
  const [filterStatus, setFilterStatus] = useState('all')
  const [showArchived, setShowArchived] = useState(false)
  const [creating, setCreating] = useState(false)

  const returns = useApi(
    showArchived ? '/returns?includeArchived=1' : '/returns',
  )
  const purchases = useApi('/purchases')
  const sales = useApi('/sales')
  const varieties = useApi('/varieties')

  // nueva devolución form
  const [type, setType] = useState('purchase')
  const [referenceId, setReferenceId] = useState('')
  const [reason, setReason] = useState('')
  const [items, setItems] = useState([{ ...EMPTY_RETURN_ITEM }])
  // Doc: foto-evidencia. Guardamos un array de objetos {name, dataUrl}
  // — se persisten como base64 en el campo `photos` de la devolución.
  const [photos, setPhotos] = useState([])

  // resolve form state — keyed by return id
  const [resolveFor, setResolveFor] = useState(null)
  const [resolutionType, setResolutionType] = useState('deduct')
  const [resolveItems, setResolveItems] = useState([])

  // review/confirm state
  const [reviewFor, setReviewFor] = useState(null)
  const [reviewItems, setReviewItems] = useState([])

  // Reporte: cuando reportFor está seteado se muestra el modal imprimible.
  // reportData se carga via /api/returns/:id/report cuando se abre.
  const [reportFor, setReportFor] = useState(null)
  const [reportData, setReportData] = useState(null)
  const [reportLoading, setReportLoading] = useState(false)
  const openReport = async (r) => {
    setReportFor(r)
    setReportData(null)
    setReportLoading(true)
    try {
      const data = await apiCall(`/returns/${r.id}/report`)
      setReportData(data)
    } catch (err) {
      toast.error(err.message || 'No se pudo generar el reporte')
      setReportFor(null)
    } finally {
      setReportLoading(false)
    }
  }
  const closeReport = () => {
    setReportFor(null)
    setReportData(null)
  }

  const create = useMutation((payload) =>
    apiCall('/returns', { method: 'POST', body: payload }),
  )
  const setStatus = useMutation(({ id, status, note, items }) =>
    apiCall(`/returns/${id}/status`, {
      method: 'POST',
      body: { status, note, ...(items ? { items } : {}) },
    }),
  )
  const resolve = useMutation(({ id, resolutionType, items, note }) =>
    apiCall(`/returns/${id}/resolve`, {
      method: 'POST',
      body: { resolutionType, items, note },
    }),
  )
  const compost = useMutation((id) => apiCall(`/returns/${id}/compost`, { method: 'POST' }))
  const archive = useMutation((id) =>
    apiCall(`/returns/${id}/archive`, { method: 'POST' }),
  )
  const unarchive = useMutation((id) =>
    apiCall(`/returns/${id}/unarchive`, { method: 'POST' }),
  )

  const filtered = useMemo(() => {
    const list = returns.data || []
    return list.filter(
      (r) =>
        (filterType === 'all' || r.type === filterType) &&
        (filterStatus === 'all' || r.status === filterStatus),
    )
  }, [returns.data, filterType, filterStatus])

  const references = useMemo(() => {
    if (type === 'purchase') return (purchases.data || []).map((p) => ({ id: p.id, label: `${p.id} — ${p.supplierName || '—'}` }))
    return (sales.data || []).map((s) => ({ id: s.id, label: `${s.id} — ${s.clientId}` }))
  }, [type, purchases.data, sales.data])

  const addItem = () => setItems((l) => [...l, { ...EMPTY_RETURN_ITEM }])
  const removeItem = (i) =>
    setItems((l) => (l.length > 1 ? l.filter((_, idx) => idx !== i) : l))
  const patchItem = (i, patch) =>
    setItems((l) => l.map((it, idx) => (idx === i ? { ...it, ...patch } : it)))

  const resetForm = () => {
    setType('purchase')
    setReferenceId('')
    setReason('')
    setItems([{ ...EMPTY_RETURN_ITEM }])
    setPhotos([])
  }

  /**
   * Autocarga los items de la OC o Venta seleccionada en el formulario.
   * Doc: "Cuando seleccione la orden de compra o venta de la devolución,
   * deberían salir automáticamente los ítems que incluye dicha orden y
   * seleccionar de ahí."
   */
  const autofillItemsFromReference = (refType, refId) => {
    if (!refId) {
      setItems([{ ...EMPTY_RETURN_ITEM }])
      return
    }
    const list = refType === 'purchase' ? purchases.data : sales.data
    const ref = (list || []).find((r) => r.id === refId)
    if (!ref) {
      setItems([{ ...EMPTY_RETURN_ITEM }])
      return
    }
    const raw = ref.items || []
    if (raw.length === 0) {
      setItems([{ ...EMPTY_RETURN_ITEM }])
      return
    }
    setItems(
      raw.map((it) => ({
        variety: it.variety || '',
        stemLength: Number(it.stemLength) || 60,
        // Cantidad inicial = lo pedido/vendido. El operador la baja al
        // monto realmente devuelto.
        quantity: Number(it.quantity) || 0,
        productCondition: 'acceptable',
        reason: '',
      })),
    )
  }

  /**
   * Lectura del input file → base64. Sin uploads reales — los archivos
   * viajan en el JSON del POST. Es funcional para pocas fotos pequeñas
   * (al persistir en data.json el límite práctico está en ~MB por
   * devolución). Para volumen alto el siguiente paso sería un endpoint
   * de subida y guardar la URL.
   */
  const handlePhotos = (fileList) => {
    const files = Array.from(fileList || [])
    if (files.length === 0) return
    Promise.all(
      files.map(
        (f) =>
          new Promise((resolve) => {
            const reader = new FileReader()
            reader.onload = () =>
              resolve({ name: f.name, size: f.size, dataUrl: reader.result })
            reader.readAsDataURL(f)
          }),
      ),
    ).then((entries) => setPhotos((p) => [...p, ...entries]))
  }

  const onSubmit = async (e) => {
    e.preventDefault()
    if (!referenceId) return toast.error('Selecciona la orden de referencia')
    if (items.some((it) => !it.variety || Number(it.quantity) <= 0)) {
      return toast.error('Cada item necesita variedad y cantidad > 0')
    }
    try {
      await create.mutate({
        type,
        referenceId,
        // Por compat se mantiene `reason` como motivo global; cada item
        // además lleva su propio motivo (preferido por el operador para
        // la trazabilidad de qué falló por SKU).
        reason,
        photos,
        items: items.map((it) => ({
          variety: it.variety,
          quantity: Number(it.quantity),
          stemLength: Number(it.stemLength) || 60,
          productCondition: it.productCondition,
          reason: it.reason || '',
        })),
      })
      toast.success('Devolución creada')
      setCreating(false)
      resetForm()
      returns.refresh()
    } catch (err) {
      toast.error(err.message)
    }
  }

  const onAdvance = async (r, status, note) => {
    try {
      await setStatus.mutate({ id: r.id, status, note })
      toast.success(`${r.id} → ${RETURN_STATUS_LABELS[status] || status}`)
      returns.refresh()
    } catch (err) {
      toast.error(err.message)
    }
  }

  const onCompost = async (r) => {
    if (!confirm(`¿Compostar ${r.id}? (${r.daysOpen} días sin recoger)`)) return
    try {
      await compost.mutate(r.id)
      toast.success('Devolución compostada')
      returns.refresh()
    } catch (err) {
      toast.error(err.message)
    }
  }

  const onArchive = async (id) => {
    if (!confirm('¿Archivar esta devolución? Se ocultará del listado por defecto.')) return
    try {
      await archive.mutate(id)
      toast.success('Devolución archivada')
      returns.refresh()
    } catch (err) {
      toast.error(err.message)
    }
  }

  const onUnarchive = async (id) => {
    try {
      await unarchive.mutate(id)
      toast.success('Devolución restaurada')
      returns.refresh()
    } catch (err) {
      toast.error(err.message)
    }
  }

  const openReview = (r) => {
    setReviewFor(r)
    // Hidratar el form de review. `requestedQuantity` se preserva como
    // referencia (lo solicitado inicialmente) para que el operario vea
    // si está bajando la cantidad y poder forzar un motivo. El
    // `reason` viene del catálogo cerrado RETURN_REASONS.
    setReviewItems(
      (r.items || []).map((it) => ({
        variety: it.variety,
        stemLength: it.stemLength,
        requestedQuantity: Number(it.quantity) || 0,
        quantity: Number(it.quantity) || 0,
        productCondition: it.productCondition || 'acceptable',
        reason: it.reason || '',
      })),
    )
  }

  const submitReview = async (e) => {
    e.preventDefault()
    // Validación: si el operario bajó la cantidad respecto a la
    // solicitada, debe seleccionar un motivo del catálogo. El motivo
    // es obligatorio para que el reporte al proveedor explique por
    // qué no se aceptó la cantidad completa.
    const missing = reviewItems.find(
      (it) =>
        Number(it.quantity) < Number(it.requestedQuantity) && !it.reason,
    )
    if (missing) {
      return toast.error(
        `Selecciona un motivo para ${missing.variety} ${missing.stemLength}cm (no se aceptó la cantidad completa).`,
      )
    }
    try {
      // Una sola llamada: el endpoint /status acepta `items` opcional para
      // actualizar producto/cantidades al mismo tiempo que se mueve el estado.
      // (Antes había un PUT /returns/:id aparte que caía en 404.)
      await setStatus.mutate({
        id: reviewFor.id,
        status: 'confirmed',
        note: 'Producto y cantidades confirmados',
        items: reviewItems.map((it) => ({
          variety: it.variety,
          stemLength: Number(it.stemLength) || 60,
          quantity: Number(it.quantity) || 0,
          productCondition: it.productCondition || 'acceptable',
          reason: it.reason || '',
        })),
      })
      toast.success('Devolución confirmada')
      setReviewFor(null)
      returns.refresh()
    } catch (err) {
      toast.error(err.message)
    }
  }

  const openResolve = (r) => {
    setResolveFor(r)
    // default resolution by type
    const defaultRes = r.type === 'purchase' ? 'deduct' : 'restock_cold'
    setResolutionType(defaultRes)
    setResolveItems(
      (r.items || []).map((it) => ({
        variety: it.variety,
        stemLength: it.stemLength,
        quantity: it.quantity,
        stage: RETURN_RESOLUTIONS[defaultRes]?.stage || 'unprocessed',
      })),
    )
  }

  const submitResolve = async (e) => {
    e.preventDefault()
    try {
      await resolve.mutate({
        id: resolveFor.id,
        resolutionType,
        items: resolveItems,
        note: `Resuelto como ${RETURN_RESOLUTIONS[resolutionType]?.label}`,
      })
      toast.success('Devolución resuelta')
      setResolveFor(null)
      returns.refresh()
    } catch (err) {
      toast.error(err.message)
    }
  }

  const selectedResolution = RETURN_RESOLUTIONS[resolutionType]
  const stageNeeded = selectedResolution && selectedResolution.sign !== 0

  return (
    <div>
      <PageHeader
        title="Devoluciones"
        subtitle="Flujo con estados, línea del tiempo y compostaje a 5 días"
        icon={RotateCcw}
        action={
          <Button onClick={() => setCreating(true)}>
            <Plus size={20} />
            <span>Nueva devolución</span>
          </Button>
        }
      />

      {/* Filters */}
      <div className="flex gap-3 mb-4 flex-wrap">
        <Select
          className="max-w-xs"
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
        >
          <option value="all">Todos los tipos</option>
          {RETURN_TYPES.map((t) => (
            <option key={t} value={t}>
              {RETURN_TYPE_LABELS[t]}
            </option>
          ))}
        </Select>
        <Select
          className="max-w-xs"
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
        >
          <option value="all">Todos los estados</option>
          {RETURN_STATUSES.map((s) => (
            <option key={s} value={s}>
              {RETURN_STATUS_LABELS[s]}
            </option>
          ))}
        </Select>
        <label className="flex items-center gap-2 text-sm text-gray-700 px-2 cursor-pointer select-none">
          <input
            type="checkbox"
            className="rounded border-gray-300"
            checked={showArchived}
            onChange={(e) => setShowArchived(e.target.checked)}
          />
          Mostrar archivadas
        </label>
      </div>

      {returns.loading && <p className="text-gray-500">Cargando…</p>}
      {returns.error && <p className="text-red-600">Error: {returns.error.message}</p>}
      {returns.data && (
        <ExpandableCards
          items={filtered}
          getKey={(r) => r.id}
          emptyText="Sin devoluciones"
          renderHeader={(r) => (
            <div className="flex items-center gap-4 flex-wrap">
              <span className="font-semibold text-expo-dark">{r.id}</span>
              <span
                className={cn(
                  'px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase',
                  TYPE_PILL[r.type] || 'bg-gray-100 text-gray-700',
                )}
              >
                {RETURN_TYPE_LABELS[r.type] || r.type}
              </span>
              <span className="text-gray-600 text-sm">ref {r.referenceId}</span>
              <span className="ml-auto text-xs text-gray-500">
                {r.daysOpen} día{r.daysOpen === 1 ? '' : 's'}
              </span>
              {r.canCompost && (
                <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase bg-expo-olive/30 text-expo-dark">
                  compostable
                </span>
              )}
              {r.archivedAt && (
                <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase bg-gray-200 text-gray-700">
                  archivada
                </span>
              )}
              <span
                className={cn(
                  'px-3 py-1 rounded-full text-xs font-semibold',
                  STATUS_PILL[r.status] || 'bg-gray-100 text-gray-700',
                )}
              >
                {RETURN_STATUS_LABELS[r.status] || r.status}
              </span>
            </div>
          )}
          renderDetails={(r) => (
            <div className="space-y-5 pt-4">
              <CardGrid cols={4}>
                <CardField label="Tipo" value={RETURN_TYPE_LABELS[r.type]} />
                <CardField label="Referencia" value={r.referenceId} />
                <CardField
                  label={r.type === 'purchase' ? 'Proveedor' : 'Cliente'}
                  value={r.supplierName || r.clientId}
                />
                <CardField label="Creada" value={fmtDate(r.createdAt)} />
              </CardGrid>

              <div>
                <div className="text-sm font-semibold text-expo-dark mb-2">Motivo</div>
                <div className="text-sm text-gray-700 bg-white p-3 rounded-xl border border-gray-200">
                  {r.reason || '—'}
                </div>
              </div>

              <div>
                <div className="text-sm font-semibold text-expo-dark mb-2">Items</div>
                <div className="bg-white rounded-xl border border-gray-200 divide-y divide-gray-100">
                  {(r.items || []).map((it, i) => (
                    <div
                      key={i}
                      className="grid grid-cols-4 gap-2 px-4 py-2 text-sm text-expo-dark"
                    >
                      <span className="font-medium">{it.variety}</span>
                      <span>{it.stemLength}cm</span>
                      <span>{it.quantity} tallos</span>
                      <span className="text-right text-gray-500">
                        {it.productCondition || '—'}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Timeline */}
              <div>
                <div className="text-sm font-semibold text-expo-dark mb-2">
                  Línea del tiempo
                </div>
                <ol className="relative border-l-2 border-gray-200 ml-3 space-y-4 pl-5">
                  {(r.statusHistory || []).map((h, i) => (
                    <li key={i} className="relative">
                      <span
                        className={cn(
                          'absolute -left-[29px] top-1 w-4 h-4 rounded-full border-2 border-white shadow',
                          TIMELINE_DOT[h.status] || 'bg-gray-400',
                        )}
                      />
                      <div className="text-sm font-semibold text-expo-dark">
                        {RETURN_STATUS_LABELS[h.status] || h.status}
                      </div>
                      <div className="text-xs text-gray-500">
                        {new Date(h.at).toLocaleString('es-CO')}
                        {h.by ? ` · por ${h.by}` : ''}
                      </div>
                      {h.note && (
                        <div className="text-xs text-gray-600 italic mt-0.5">
                          {h.note}
                        </div>
                      )}
                    </li>
                  ))}
                </ol>
              </div>

              {/* Acciones por estado */}
              <div className="flex gap-2 flex-wrap pt-2 border-t border-gray-100">
                {!r.archivedAt && r.status === 'requested' && (
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() =>
                      onAdvance(r, 'in_review', 'Equipo recibiendo devolución')
                    }
                  >
                    <ChevronsRight size={16} />
                    <span>Marcar en revisión</span>
                  </Button>
                )}
                {!r.archivedAt && r.status === 'in_review' && (
                  <Button type="button" variant="secondary" onClick={() => openReview(r)}>
                    <CheckCircle2 size={16} />
                    <span>Confirmar producto y cantidades</span>
                  </Button>
                )}
                {!r.archivedAt && r.status === 'confirmed' && (
                  <Button type="button" onClick={() => openResolve(r)}>
                    <CheckCircle2 size={16} />
                    <span>Resolver (aplicar inventario)</span>
                  </Button>
                )}
                {!r.archivedAt && r.canCompost && r.status !== 'composted' && r.status !== 'resolved' && (
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => onCompost(r)}
                    className="text-expo-olive border-expo-olive"
                  >
                    <Leaf size={16} />
                    <span>Compostar</span>
                  </Button>
                )}
                {!r.archivedAt && !['resolved', 'composted', 'cancelled'].includes(r.status) && (
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => onAdvance(r, 'cancelled', 'Cancelada manualmente')}
                  >
                    <X size={16} />
                    <span>Cancelar</span>
                  </Button>
                )}
                {/* Doc: el reporte se genera SOLO cuando la devolución
                    está resuelta. Incluye solicitud inicial vs confirmada,
                    motivos, fotos y datos del proveedor/cliente. */}
                {(r.status === 'resolved' || r.status === 'composted') && (
                  <Button
                    type="button"
                    onClick={() => openReport(r)}
                  >
                    <FileText size={16} />
                    <span>Generar reporte</span>
                  </Button>
                )}
                {r.archivedAt ? (
                  <Button
                    type="button"
                    variant="secondary"
                    className="ml-auto"
                    onClick={() => onUnarchive(r.id)}
                  >
                    <ArchiveRestore size={16} />
                    <span>Desarchivar</span>
                  </Button>
                ) : (
                  <Button
                    type="button"
                    variant="secondary"
                    className="ml-auto"
                    onClick={() => onArchive(r.id)}
                  >
                    <Archive size={16} />
                    <span>Archivar</span>
                  </Button>
                )}
              </div>
            </div>
          )}
        />
      )}

      {/* ── Modal: Nueva devolución ──────────────────────────────── */}
      <Modal
        open={creating}
        onClose={() => {
          setCreating(false)
          resetForm()
        }}
        title="Nueva devolución"
        size="2xl"
      >
        <form onSubmit={onSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Tipo" required>
              <Select
                value={type}
                onChange={(e) => {
                  setType(e.target.value)
                  setReferenceId('')
                  setItems([{ ...EMPTY_RETURN_ITEM }])
                }}
              >
                {RETURN_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {RETURN_TYPE_LABELS[t]}
                  </option>
                ))}
              </Select>
            </Field>
            <Field
              label={type === 'purchase' ? 'Orden de compra' : 'Venta'}
              required
              hint="Al seleccionar, se autocargan los items de la orden."
            >
              <Select
                value={referenceId}
                onChange={(e) => {
                  const v = e.target.value
                  setReferenceId(v)
                  autofillItemsFromReference(type, v)
                }}
                required
              >
                <option value="">Selecciona…</option>
                {references.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.label}
                  </option>
                ))}
              </Select>
            </Field>
          </div>

          <Field label="Motivo general (opcional)">
            <Input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Si todos los items comparten razón; si no, usa el motivo por item."
            />
          </Field>

          {/* Doc: foto-evidencia. Sirve para anexar al reporte que se
              envía al proveedor. */}
          <Field label="Fotos de evidencia">
            <input
              type="file"
              accept="image/*"
              multiple
              onChange={(e) => handlePhotos(e.target.files)}
              className="block text-sm"
            />
            {photos.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-2">
                {photos.map((p, i) => (
                  <div
                    key={i}
                    className="relative rounded-lg border border-gray-200 overflow-hidden bg-white"
                  >
                    <img
                      src={p.dataUrl}
                      alt={p.name}
                      className="h-16 w-16 object-cover"
                    />
                    <button
                      type="button"
                      onClick={() =>
                        setPhotos((arr) => arr.filter((_, idx) => idx !== i))
                      }
                      className="absolute -top-1 -right-1 bg-red-600 text-white rounded-full w-5 h-5 text-xs flex items-center justify-center"
                      title="Quitar"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}
          </Field>

          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-expo-dark">Items</span>
              <button
                type="button"
                onClick={addItem}
                className="text-sm text-expo-primary hover:text-expo-dark font-medium flex items-center gap-1"
              >
                <Plus size={16} />
                Añadir item
              </button>
            </div>
            <div className="space-y-2">
              {/* Cabecera con labels — visibles para cada columna del
                  editor. Antes solo había placeholders en cada input,
                  lo que dificultaba reconocer rápido cada campo. */}
              <div className="grid grid-cols-12 gap-2 px-3 pb-1 text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                <div className="col-span-3">Variedad</div>
                <div className="col-span-2">Cantidad</div>
                <div className="col-span-1">Long. cm</div>
                <div className="col-span-2">Condición</div>
                <div className="col-span-3">Motivo</div>
                <div className="col-span-1" />
              </div>
              {items.map((it, idx) => (
                <div
                  key={idx}
                  className="grid grid-cols-12 gap-2 bg-expo-light/50 p-3 rounded-xl items-start"
                >
                  <div className="col-span-3">
                    <Select
                      value={it.variety}
                      onChange={(e) => patchItem(idx, { variety: e.target.value })}
                      required
                    >
                      <option value="">Variedad…</option>
                      {(varieties.data || []).map((v) => (
                        <option key={v} value={v}>
                          {v}
                        </option>
                      ))}
                    </Select>
                  </div>
                  <div className="col-span-2">
                    <Input
                      type="number"
                      min="0"
                      value={it.quantity}
                      onChange={(e) => patchItem(idx, { quantity: e.target.value })}
                      placeholder="Tallos"
                    />
                  </div>
                  <div className="col-span-1">
                    <Input
                      type="number"
                      min="0"
                      value={it.stemLength}
                      onChange={(e) => patchItem(idx, { stemLength: e.target.value })}
                      placeholder="cm"
                    />
                  </div>
                  <div className="col-span-2">
                    <Select
                      value={it.productCondition}
                      onChange={(e) =>
                        patchItem(idx, { productCondition: e.target.value })
                      }
                    >
                      <option value="acceptable">Aceptable</option>
                      <option value="damaged">Dañado</option>
                      <option value="rotten">Podrido</option>
                    </Select>
                  </div>
                  <div className="col-span-3">
                    {/* Motivo de devolución por item — del catálogo
                        cerrado RETURN_REASONS (no texto libre). */}
                    <Select
                      value={it.reason || ''}
                      onChange={(e) => patchItem(idx, { reason: e.target.value })}
                    >
                      <option value="">— Selecciona motivo —</option>
                      {RETURN_REASONS.map((reason) => (
                        <option key={reason.id} value={reason.id}>
                          {reason.label}
                        </option>
                      ))}
                    </Select>
                  </div>
                  <div className="col-span-1 flex justify-end">
                    <button
                      type="button"
                      onClick={() => removeItem(idx)}
                      disabled={items.length === 1}
                      className="p-2 rounded-lg text-red-600 hover:bg-red-50 disabled:opacity-30"
                    >
                      <X size={16} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="flex justify-end gap-3">
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                setCreating(false)
                resetForm()
              }}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={create.loading}>
              {create.loading ? 'Creando…' : 'Crear devolución'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* ── Modal: Confirmar (review) ─────────────────────────────
       *
       * Para cada item el operario:
       *   1) Ajusta la cantidad confirmada (puede ser menor a la
       *      solicitada — el solicitado se muestra en gris al lado).
       *   2) Marca la condición del producto.
       *   3) Selecciona un motivo del catálogo cerrado. Si reduce la
       *      cantidad debe forzosamente elegir motivo; si acepta la
       *      cantidad completa el motivo es opcional pero recomendado
       *      (queda como motivo del registro).
       */}
      <Modal
        open={Boolean(reviewFor)}
        onClose={() => setReviewFor(null)}
        title={reviewFor ? `Confirmar ${reviewFor.id}` : ''}
        size="2xl"
      >
        {reviewFor && (
          <form onSubmit={submitReview} className="space-y-4">
            <div className="text-sm text-gray-600">
              Ajusta cantidades, condición y motivo por cada item antes de
              mover a <strong>confirmada</strong>. Si rebajas la cantidad
              solicitada, el motivo es obligatorio — quedará en el reporte
              que se envía al proveedor.
            </div>
            <div className="space-y-2">
              {/* Cabecera */}
              <div className="grid grid-cols-12 gap-2 px-3 pb-1 text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                <div className="col-span-3">Item</div>
                <div className="col-span-2">Solicitado</div>
                <div className="col-span-2">Confirmado</div>
                <div className="col-span-2">Condición</div>
                <div className="col-span-3">Motivo</div>
              </div>
              {reviewItems.map((it, idx) => {
                const reduced = Number(it.quantity) < Number(it.requestedQuantity)
                const needsReason = reduced && !it.reason
                return (
                  <div
                    key={idx}
                    className={cn(
                      'grid grid-cols-12 gap-2 p-3 rounded-xl items-center',
                      needsReason ? 'bg-amber-50 border border-amber-200' : 'bg-expo-light/50',
                    )}
                  >
                    <div className="col-span-3 text-sm font-medium text-expo-dark">
                      {it.variety} — {it.stemLength}cm
                    </div>
                    <div className="col-span-2 text-sm text-gray-500">
                      {Number(it.requestedQuantity).toLocaleString('es-CO')}
                    </div>
                    <div className="col-span-2">
                      <Input
                        type="number"
                        min="0"
                        value={it.quantity}
                        onChange={(e) =>
                          setReviewItems((l) =>
                            l.map((r, i) =>
                              i === idx ? { ...r, quantity: Number(e.target.value) } : r,
                            ),
                          )
                        }
                      />
                    </div>
                    <div className="col-span-2">
                      <Select
                        value={it.productCondition}
                        onChange={(e) =>
                          setReviewItems((l) =>
                            l.map((r, i) =>
                              i === idx ? { ...r, productCondition: e.target.value } : r,
                            ),
                          )
                        }
                      >
                        <option value="acceptable">Aceptable</option>
                        <option value="damaged">Dañado</option>
                        <option value="rotten">Podrido</option>
                      </Select>
                    </div>
                    <div className="col-span-3">
                      <Select
                        value={it.reason}
                        onChange={(e) =>
                          setReviewItems((l) =>
                            l.map((r, i) =>
                              i === idx ? { ...r, reason: e.target.value } : r,
                            ),
                          )
                        }
                      >
                        <option value="">
                          {reduced ? '— Selecciona motivo —' : '— Sin motivo —'}
                        </option>
                        {RETURN_REASONS.map((reason) => (
                          <option key={reason.id} value={reason.id}>
                            {reason.label}
                          </option>
                        ))}
                      </Select>
                    </div>
                  </div>
                )
              })}
            </div>
            <div className="flex justify-end gap-3">
              <Button type="button" variant="secondary" onClick={() => setReviewFor(null)}>
                Cancelar
              </Button>
              <Button type="submit">Confirmar devolución</Button>
            </div>
          </form>
        )}
      </Modal>

      {/* ── Modal: Resolver ──────────────────────────────────────── */}
      <Modal
        open={Boolean(resolveFor)}
        onClose={() => setResolveFor(null)}
        title={resolveFor ? `Resolver ${resolveFor.id}` : ''}
        size="lg"
      >
        {resolveFor && (
          <form onSubmit={submitResolve} className="space-y-4">
            <Field label="Tipo de resolución" required>
              <Select
                value={resolutionType}
                onChange={(e) => {
                  const next = e.target.value
                  setResolutionType(next)
                  const stage = RETURN_RESOLUTIONS[next]?.stage || 'unprocessed'
                  setResolveItems((l) => l.map((it) => ({ ...it, stage })))
                }}
              >
                {Object.entries(RETURN_RESOLUTIONS).map(([id, r]) => (
                  <option key={id} value={id}>
                    {r.label}
                    {r.sign > 0 ? ' (+)' : r.sign < 0 ? ' (–)' : ''}
                  </option>
                ))}
              </Select>
            </Field>
            <div className="text-xs text-gray-600 bg-expo-light/50 p-3 rounded-lg">
              {selectedResolution?.sign > 0 && (
                <>Aumenta el inventario en la etapa seleccionada.</>
              )}
              {selectedResolution?.sign < 0 && (
                <>Descuenta tallos del inventario (etapa seleccionada).</>
              )}
              {selectedResolution?.sign === 0 && (
                <>No se modifica el inventario (solo cierra la devolución).</>
              )}
            </div>
            {stageNeeded && (
              <div className="space-y-2">
                {resolveItems.map((it, idx) => (
                  <div
                    key={idx}
                    className="grid grid-cols-12 gap-2 bg-expo-light/50 p-3 rounded-xl items-center"
                  >
                    <div className="col-span-4 text-sm font-medium text-expo-dark">
                      {it.variety} — {it.stemLength}cm
                    </div>
                    <div className="col-span-4">
                      <Input
                        type="number"
                        min="0"
                        value={it.quantity}
                        onChange={(e) =>
                          setResolveItems((l) =>
                            l.map((r, i) =>
                              i === idx ? { ...r, quantity: Number(e.target.value) } : r,
                            ),
                          )
                        }
                      />
                    </div>
                    <div className="col-span-4">
                      <Select
                        value={it.stage}
                        onChange={(e) =>
                          setResolveItems((l) =>
                            l.map((r, i) => (i === idx ? { ...r, stage: e.target.value } : r)),
                          )
                        }
                      >
                        {INVENTORY_STAGES.map((s) => (
                          <option key={s} value={s}>
                            {INVENTORY_STAGE_LABELS[s]}
                          </option>
                        ))}
                      </Select>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <div className="flex justify-end gap-3">
              <Button type="button" variant="secondary" onClick={() => setResolveFor(null)}>
                Cancelar
              </Button>
              <Button type="submit" disabled={resolve.loading}>
                {resolve.loading ? 'Resolviendo…' : 'Resolver devolución'}
              </Button>
            </div>
          </form>
        )}
      </Modal>

      {/* ── Modal: Reporte de devolución ─────────────────────────────
       *
       * Vista imprimible (Print → guardar como PDF) con la información
       * completa: cabecera de la devolución, datos del proveedor/cliente,
       * solicitud inicial vs confirmada por item con motivos y razones,
       * resolución aplicada, fotos y línea de tiempo.
       */}
      <Modal
        open={Boolean(reportFor)}
        onClose={closeReport}
        title={reportFor ? `Reporte de devolución · ${reportFor.id}` : ''}
        size="xl"
      >
        {reportLoading && <p className="text-gray-500">Generando reporte…</p>}
        {reportData && <ReturnReport data={reportData} onClose={closeReport} />}
      </Modal>
    </div>
  )
}

/**
 * Vista imprimible del reporte de devolución. Estiliza para impresión:
 * fondo blanco, bordes finos, oculta los botones cuando se imprime. El
 * usuario hace click en "Imprimir / Guardar como PDF" y el navegador
 * dispara el diálogo nativo, donde puede elegir "Guardar como PDF".
 */
function ReturnReport({ data, onClose }) {
  const photos = Array.isArray(data.photos) ? data.photos : []
  return (
    <div>
      {/* Controles — se ocultan al imprimir */}
      <div className="flex gap-2 mb-4 print:hidden">
        <Button type="button" onClick={() => window.print()}>
          <Printer size={16} />
          <span>Imprimir / Guardar como PDF</span>
        </Button>
        <Button type="button" variant="secondary" onClick={onClose}>
          Cerrar
        </Button>
      </div>

      {/* CSS específico de impresión — limita el contenido al reporte. */}
      <style>{`
        @media print {
          body * { visibility: hidden; }
          #return-report, #return-report * { visibility: visible; }
          #return-report { position: absolute; left: 0; top: 0; width: 100%; padding: 16px; }
        }
      `}</style>

      <div
        id="return-report"
        className="bg-white text-expo-dark border border-gray-200 rounded-xl p-6 text-sm"
      >
        {/* Cabecera */}
        <div className="flex items-start justify-between gap-6 border-b border-gray-200 pb-4 mb-4">
          <div>
            <h1 className="text-xl font-bold">Reporte de Devolución</h1>
            <div className="text-gray-500 text-xs uppercase tracking-wide mt-1">
              {data.typeLabel} · {data.statusLabel}
            </div>
          </div>
          <div className="text-right">
            <div className="text-2xl font-bold">{data.id}</div>
            <div className="text-xs text-gray-500">
              Creada: {new Date(data.createdAt).toLocaleString('es-CO')}
            </div>
            {data.resolvedAt && (
              <div className="text-xs text-gray-500">
                Resuelta: {new Date(data.resolvedAt).toLocaleString('es-CO')}
              </div>
            )}
          </div>
        </div>

        {/* Datos de referencia */}
        <div className="grid grid-cols-2 gap-6 mb-4">
          <div>
            <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
              {data.type === 'purchase'
                ? 'Orden de compra'
                : 'Venta de referencia'}
            </div>
            <div className="font-semibold">
              {data.reference?.id || '—'}
              {data.reference?.date && (
                <span className="text-gray-500 text-xs ml-2">
                  ({new Date(data.reference.date).toLocaleDateString('es-CO')})
                </span>
              )}
            </div>
          </div>
          <div>
            <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
              {data.counterparty?.kind === 'supplier' ? 'Proveedor' : 'Cliente'}
            </div>
            <div className="font-semibold">
              {data.counterparty?.name || '—'}
            </div>
            {data.counterparty?.principalName && (
              <div className="text-xs text-gray-500">
                Principal: {data.counterparty.principalName}
              </div>
            )}
          </div>
        </div>

        {/* Motivo general */}
        {data.reason && (
          <div className="mb-4 rounded-lg border border-gray-200 bg-gray-50 p-3">
            <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
              Motivo general
            </div>
            <div>{data.reason}</div>
          </div>
        )}

        {/* Solicitud inicial vs confirmado */}
        <div className="mb-4">
          <div className="text-sm font-semibold uppercase tracking-wide text-gray-500 mb-2">
            Detalle por producto
          </div>
          <table className="w-full text-xs border border-gray-200 rounded-lg overflow-hidden">
            <thead className="bg-gray-100 text-left">
              <tr>
                <th className="px-3 py-2">Variedad</th>
                <th className="px-3 py-2">Long.</th>
                <th className="px-3 py-2 text-right">Solicitado</th>
                <th className="px-3 py-2 text-right">Confirmado</th>
                <th className="px-3 py-2 text-right">Diferencia</th>
                <th className="px-3 py-2">Condición</th>
                <th className="px-3 py-2">Motivo / razón</th>
              </tr>
            </thead>
            <tbody>
              {data.itemsBreakdown.map((row, i) => (
                <tr key={i} className="border-t border-gray-200 align-top">
                  <td className="px-3 py-2 font-medium">{row.variety}</td>
                  <td className="px-3 py-2">
                    {row.stemLength != null ? `${row.stemLength}cm` : '—'}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {Number(row.requestedQuantity).toLocaleString('es-CO')}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {Number(row.confirmedQuantity).toLocaleString('es-CO')}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {row.delta !== 0 ? row.delta : '—'}
                  </td>
                  <td className="px-3 py-2">{row.confirmedCondition || '—'}</td>
                  <td className="px-3 py-2 text-gray-700">
                    {/* El reason es un id del catálogo RETURN_REASONS;
                        en el reporte lo traducimos al label legible.
                        Si es texto libre legacy se imprime tal cual. */}
                    {(() => {
                      const id = row.confirmedReason || row.requestedReason
                      if (!id) return '—'
                      return RETURN_REASON_LABELS[id] || id
                    })()}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot className="bg-gray-50 font-semibold">
              <tr>
                <td className="px-3 py-2" colSpan={2}>
                  Totales
                </td>
                <td className="px-3 py-2 text-right">
                  {Number(data.totals?.requested || 0).toLocaleString('es-CO')}
                </td>
                <td className="px-3 py-2 text-right">
                  {Number(data.totals?.confirmed || 0).toLocaleString('es-CO')}
                </td>
                <td className="px-3 py-2 text-right" colSpan={3}>
                  Δ {(data.totals?.requested || 0) - (data.totals?.confirmed || 0)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>

        {/* Resolución */}
        {data.resolutionLabel && (
          <div className="mb-4 rounded-lg border border-emerald-200 bg-emerald-50 p-3">
            <div className="text-xs font-semibold uppercase tracking-wide text-emerald-800 mb-1">
              Resolución aplicada
            </div>
            <div className="text-emerald-900">{data.resolutionLabel}</div>
          </div>
        )}

        {/* Fotos */}
        {photos.length > 0 && (
          <div className="mb-4">
            <div className="text-sm font-semibold uppercase tracking-wide text-gray-500 mb-2">
              Fotos de evidencia
            </div>
            <div className="grid grid-cols-3 gap-3">
              {photos.map((p, i) => (
                <div key={i} className="border border-gray-200 rounded-lg overflow-hidden">
                  <img
                    src={p.dataUrl}
                    alt={p.name || `evidencia-${i + 1}`}
                    className="w-full h-40 object-cover"
                  />
                  <div className="px-2 py-1 text-[10px] text-gray-500 truncate">
                    {p.name || `evidencia-${i + 1}`}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Línea del tiempo */}
        <div>
          <div className="text-sm font-semibold uppercase tracking-wide text-gray-500 mb-2">
            Línea del tiempo
          </div>
          <ol className="space-y-2">
            {(data.statusHistory || []).map((h, i) => (
              <li key={i} className="text-xs flex items-start gap-3 border-l-2 border-gray-200 pl-3">
                <div className="min-w-[110px] text-gray-500">
                  {new Date(h.at).toLocaleString('es-CO')}
                </div>
                <div className="flex-1">
                  <div className="font-semibold">{h.status}</div>
                  {h.note && <div className="text-gray-600">{h.note}</div>}
                  {h.by && <div className="text-gray-400">por {h.by}</div>}
                </div>
              </li>
            ))}
          </ol>
        </div>

        <div className="mt-6 pt-3 border-t border-gray-200 text-[10px] text-gray-400 text-center">
          Reporte generado automáticamente desde el sistema Expogreens.
        </div>
      </div>
    </div>
  )
}
